import onetime from "@-/com.npmjs.onetime";
import { addAppLogListener, type AppLogRecord } from "@-/dev.improve.haniverse.lib.logger";
import type { DiagnosticsLogEntry, DiagnosticsLogLevel } from "#diagnostics-types";
import { formatDiagnosticsError, formatLogArguments } from "#format-log-arguments";

const CONSOLE_METHODS = ["debug", "error", "info", "log", "trace", "warn"] as const;
type ConsoleMethod = (typeof CONSOLE_METHODS)[number];

const CONSOLE_METHOD_LEVEL: Record<ConsoleMethod, DiagnosticsLogLevel> = {
  debug: "debug",
  error: "error",
  info: "info",
  log: "info",
  trace: "trace",
  warn: "warn",
};

function appLogToEntry(record: AppLogRecord): void {
  const formatted = formatLogArguments(
    record.data === undefined ? record.messages : [...record.messages, record.data],
  );
  emit({
    error: formatDiagnosticsError(record.error) ?? formatted.error,
    level: record.level,
    message: formatted.message,
    source: "app",
    timestamp: record.timestamp,
  });
}

function installAppLogListener(): () => void {
  return addAppLogListener(appLogToEntry);
}

/**
 * Wraps `console.*` so DIRECT console calls (third-party libraries, React warnings, the
 * `logger()` proxy in dev builds) are captured too. The LogLayer's own console transport writes
 * through pre-bound originals, so app logs are never captured twice.
 */
function installConsoleCapture(): () => void {
  const host = globalThis.console;
  const originals = new Map<ConsoleMethod, (...args: unknown[]) => void>();
  const wrappers = new Map<ConsoleMethod, (...args: unknown[]) => void>();
  for (const method of CONSOLE_METHODS) {
    const original: unknown = Reflect.get(host, method);
    if (typeof original !== "function") {
      continue;
    }
    // oxlint-disable-next-line typescript/no-unsafe-type-assertion -- console methods are variadic
    const bound = original as (...args: unknown[]) => void;
    originals.set(method, bound);
    const wrapper = (...args: unknown[]): void => {
      try {
        const formatted = formatLogArguments(args);
        emit({
          error: formatted.error,
          level: CONSOLE_METHOD_LEVEL[method],
          message: formatted.message,
          source: "console",
          timestamp: Date.now(),
        });
      } catch {
        // Capturing must never break the console.
      }
      Reflect.apply(bound, host, args);
    };
    wrappers.set(method, wrapper);
    Reflect.set(host, method, wrapper);
  }
  return () => {
    for (const [method, original] of originals) {
      // Only restore what we installed: something else may have wrapped the console since.
      if (Reflect.get(host, method) === wrappers.get(method)) {
        Reflect.set(host, method, original);
      }
    }
  };
}

const getCaptureState = onetime(() => ({
  dispose: null as (() => void) | null,
  listeners: new Set<(entry: DiagnosticsLogEntry) => void>(),
}));

function emit(entry: DiagnosticsLogEntry): void {
  for (const listener of getCaptureState().listeners) {
    try {
      listener(entry);
    } catch {
      /* Logging must never break the app. */
    }
  }
}

/** Share one console wrapper between quick logs and full diagnostics. */
export function subscribeToCapturedLogs(
  listener: (entry: DiagnosticsLogEntry) => void,
): () => void {
  const state = getCaptureState();
  state.listeners.add(listener);
  if (state.dispose === null) {
    const stopApp = installAppLogListener();
    const stopConsole = installConsoleCapture();
    state.dispose = () => {
      stopApp();
      stopConsole();
    };
  }
  return () => {
    state.listeners.delete(listener);
    if (state.listeners.size === 0) {
      state.dispose?.();
      state.dispose = null;
    }
  };
}
